import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import ListAltIcon from '@material-ui/icons/ListAlt';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import EditIcon from '@material-ui/icons/Edit';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Cancel';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Semaphore } from 'async-mutex';
import { Colors } from '@silvergatedelivery/constants';
import { request } from 'utilities/graph';
import { createElder, adminCreateOrder, updateElder, createUploadedOrder, queryAddress } from 'graphql/mutations';
import { getRestaurantMealsByRestaurantByIsActive } from './queries';
import AutoSelect from './AutoSelect';
import FileUploader from './ExcelFileUploader';
import TargetOrderTable from './TargetOrderTable';
import OrderProblemTable from './OrderProblemTable';
import { isSubset } from 'utilities/object';
import FormDialog from 'components/FormDialog';
import { formatDatetime, formatAddress } from 'utilities/format';
import {
  getRestaurantIdSchema,
  getElderIdSchema,
  getDeliveryStaffIdSchema,
} from '../schemas';
import cache from 'utilities/cache';
import moment from 'moment-timezone';
import getExecelDataAdapter from './ExcelDataAdapter/excelDataAdapter';
import { utils, writeFile } from 'xlsx';
import { toastr } from 'react-redux-toastr';
import UploadHistoryTable from './UploadHistoryTable';
import { Storage } from 'aws-amplify';
import Marker from 'components/Map/Marker';
import { compareObjects } from 'utilities/sorting';
import { asyncListAll } from 'utilities/graph';
import { download } from 'utilities/file';

const useStyles = makeStyles((theme) => ({
  app: {
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
  },
  centerContent: {
    width: '100%',
    height: '100%',
    transition: 'width 0.3s ease-in-out',
    overflow: 'auto',
  },
  centerContentShrink: {
    width: '70%',
  },
  popup: {
    position: 'absolute',
    top: 0,
    right: '-100%',
    width: '30%',
    height: '100%',
    boxShadow: '-2px 0px 10px rgba(0, 0, 0, 0.2)',
    transition: 'right 0.3s ease-in-out',
    overflow: 'hidden',
  },
  popupActive: {
    right: 0,
  },
}));
export default function AdminCreateBulkOrderForm({ onComplete, ...props }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [targetRestaurantId, setTargetRestaurantId] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [restaurantOptions, setRestaurantOptions] = useState();
  const [elderOptions, setElderOptions] = useState();
  const [deliveryStaffOptions, setDeliveryStaffOptions] = useState();
  // const [restaurantMealOptions, setRestaurantMealOptions] = useState();
  const [restaurantMealsMap, setRestaurantMealsMap] = useState();
  const [targetOrders, setTargetOrders] = useState([]);
  const [uniqueElders, setUniqueElders] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [toCreateOrders, setToCreateOrders] = useState([]);
  const [createOrderResults, setCreateOrderResults] = useState([]);
  const [createOrderErrors, setCreateOrderErrors] = useState([]);
  const [toCreateElders, setToCreateElders] = useState([]);
  const [createElderResults, setCreateElderResults] = useState([]);
  const [createElderErrors, setCreateElderErrors] = useState([]);
  const [toUpdateElders, setToUpdateElders] = useState([]);
  const [updateElderResults, setUpdateElderResults] = useState([]);
  const [updateElderErrors, setUpdateElderErrors] = useState([]);
  const [file, setFile] = useState();
  const [checkAddressProgress, setCheckAddressProgress] = useState(0);
  const [showMap, setShowMap] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [locations, setLocations] = useState([]);
  const [orderProblems, setOrderProblems] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [needRestaurantId, setNeedRestaurantId] = useState(false);
  const [needCheckAddress, setNeedCheckAddress] = useState(false);
  const clientId = cache.get('app:facilityId');

  const createElders = async (toCreateElders) => {
    const s = new Semaphore(10);
    let results = [];
    const errors = [];
    await Promise.all(toCreateElders.map((elder) =>
      s.runExclusive(async () => {
        try {
          const { data: { createElder: data } } = await request(createElder, { input: elder });
          elder.id = data.id;
          results = [...results, data];
          setCreateElderResults(results);
        } catch (e) {
          let message = '';
          if (e.errors && e.errors[0]) {
            message = e.errors[0].message;
          }
          errors.push({
            elderId: elder.id,
            message,
          });
        }
      }),
    ));
    setCreateElderErrors(errors);
  };

  const updateElders = async (toUpdateElders) => {
    const s = new Semaphore(10);
    let results = [];
    const errors = [];
    await Promise.all(toUpdateElders.map((elder) =>
      s.runExclusive(async () => {
        try {
          const { data: { updateElder: data } } = await request(updateElder, { input: elder });
          results = [...results, data];
          setUpdateElderResults(results);
        } catch (e) {
          let message = '';
          if (e.errors && e.errors[0]) {
            message = e.errors[0].message;
          }
          errors.push({
            elderId: elder.id,
            message,
          });
        }
      }),
    ));
    setUpdateElderErrors(errors);
  };

  const createOrders = async (toCreateOrders) => {
    const s = new Semaphore(10);
    let results = [];
    const errors = [];
    await Promise.all(toCreateOrders.map((order) =>
      s.runExclusive(async () => {
        try {
          const res = await request(adminCreateOrder, { input: { orders: [order] } });
          const { data: { adminCreateOrder: { data } } } = res;
          results = [...results, ...data];
          setCreateOrderResults(results);
        } catch (e) {
          let message = '';
          if (e.errors && e.errors[0]) {
            message = e.errors[0].message;
          }
          errors.push({
            elderId: order.elderId,
            message,
          });
        }
      }),
    ));
    setCreateOrderErrors(errors);
    return results;
  };

  const createUploadHistory = async (results) => {
    const username = cache.get('app:username');
    let s3Key = `uploadedOrders/${clientId}/${file.name}-${moment().valueOf()}`;
    if (needRestaurantId) {
      s3Key = `uploadedOrders/${clientId}/${targetRestaurantId}/${file.name}-${moment().valueOf()}`;
    }
    try {
      await Storage.put(s3Key, file, {
        contentType: file.type,
      });
    } catch (e) {
      console.error(e);
    }
    const now = moment().toISOString();
    const uploadHistory = {
      // id: ID!
      clientId,
      restaurantId: needRestaurantId ? targetRestaurantId : undefined,
      orderFileS3Key: s3Key,
      fileName: file.name,
      orderIds: results.map(({ id }) => id),
      createdAt: now,
      createdBy: username,
      updatedAt: now,
      updatedBy: username,
    };

    try {
      await request(createUploadedOrder, { input: uploadHistory });
    } catch (e) {
      console.error(e);
    }
  };

  const validate = () => {
    if (needRestaurantId && !targetRestaurantId) {
      toastr.error('請先設定餐廳');
      return;
    }
    if (targetOrders.length === 0) {
      toastr.error('請上傳訂單');
      return;
    }

    return true;
  };

  const processTargetOrders = async (targetOrders) => {
    const username = cache.get('app:username');
    if (!(validate())) {
      return;
    }
    setOpenConfirmDialog(true);
    const now = moment().toISOString();
    const toCreateElders = [];
    const toUpdateElders = [];
    const allElders = [];
    uniqueElders.forEach((elder) => {
      let existingElder;
      if (elder.identificationCardId) {
        existingElder = elderOptions.find(({ identificationCardId }) => identificationCardId === elder.identificationCardId);
      } else {
        existingElder = elderOptions.find(({ name, address }) =>
          name === elder.name &&
          address.county === elder.address.county &&
          address.district === elder.address.district &&
          address.street === elder.address.street);
      }
      if (!existingElder) {
        toCreateElders.push({
          ...elder,
          clientId,
          isAllowedToEnterHome: 0,
          isDisadvantaged: 0,
          createdAt: now,
          createdBy: username,
          updatedAt: now,
          updatedBy: username,
        });
      } else {
        if (!compareObjects(existingElder, elder)) {
          const updatedElder = {
            ...existingElder,
            ...elder,
            updatedAt: now,
            updatedBy: username,
          };
          if (!isSubset(existingElder, elder)) {
            toUpdateElders.push(updatedElder);
          }
          allElders.push(updatedElder);
        } else {
          allElders.push(existingElder);
        }
      }
    });
    allElders.push(...toCreateElders);
    setElderOptions(allElders);
    setToCreateElders(toCreateElders);
    setToUpdateElders(toUpdateElders);
    await createElders(toCreateElders);
    await updateElders(toUpdateElders);
    const toCreateOrders = targetOrders.map((order) => {
      const { elder: elderInOrder } = order;
      const elder = allElders.find(({ name, address }) =>
        name === elderInOrder.name &&
        address.county === elderInOrder.address.county &&
        address.district === elderInOrder.address.district &&
        address.street === elderInOrder.address.street);
      return {
        ...order,
        restaurantId: order.restaurantId || targetRestaurantId,
        elderId: elder.id,
        elder: undefined,
        deliveryStaffName: undefined,
        restaurantName: undefined,
      };
    });
    setToCreateOrders(toCreateOrders);
    const results = await createOrders(toCreateOrders);
    await createUploadHistory(results);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await processTargetOrders(targetOrders);
    } catch (e) {
      global.logger.debug(e);
      toastr.error(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getCoordinates = async (toQueryAddress) => {
    const s = new Semaphore(1);
    let addressToCheck = toQueryAddress.length;
    setCheckAddressProgress(addressToCheck);
    await Promise.all(toQueryAddress.map((elder) =>
      s.runExclusive(async () => {
        try {
          const params = {
            input: {
              queryAddressMethod: 'tgos',
              address: formatAddress(elder.address, { includeZipCode: false, ignoreNote: true }),
            },
          };
          const { data: { queryAddress: { data } } } = await request(queryAddress, params);
          elder.address.lat = data.lat;
          elder.address.lng = data.lng;
          addressToCheck -= 1;
          setCheckAddressProgress(addressToCheck);
        } catch (e) { // TODO
          console.log(e);
        }
      }),
    ));
  };

  const checkAddress = async () => {
    if (!validate()) {
      return;
    }
    const toQueryAddress = uniqueElders.filter((elder) => !elder.address.lat || !elder.address.lng);
    if (toQueryAddress.length) {
      setIsLoading(true);
      await getCoordinates(toQueryAddress);
      setUniqueElders([...uniqueElders]);
      targetOrders.forEach(({ elder }) => {
        const updatedElder = uniqueElders.find(({ name, address }) =>
          name === elder.name &&
          address.county === elder.address.county &&
          address.district === elder.address.district &&
          address.street === elder.address.street);
        if (updatedElder) {
          elder.address.lat = updatedElder.address.lat;
          elder.address.lng = updatedElder.address.lng;
        }
      });
      setTargetOrders([...targetOrders]);
      setIsLoading(false);
    } else {
      toastr.info('所有地址都已定位檢查完成');
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  const triggerDownload = async () => {
    const rows = [];
    toCreateOrders.forEach((order) => {
      const elder = (elderOptions.find(({ id }) => id === order.elderId) || {});

      order.deliveryDatetimes.map((datetime) => {
        const matched = createOrderResults.find(({ elderId, deliveryBy }) => elderId === order.elderId && deliveryBy === datetime);
        const newRow = {};
        newRow[t('送餐對象')] = elder.name;
        newRow[`${t('送餐對象')}編號`] = elder.id;
        newRow[`訂單${t('送餐')}日期`] = formatDatetime(datetime, { weekday: false });
        rows.push({
          ...newRow,
          '訂單編號': matched ? matched.id : '',
          '錯誤訊息': (createOrderErrors.find(({ elderId }) => elderId === order.elderId) || {}).message || '',
          '除錯資訊': JSON.stringify(order),
        });
      });
    });

    const worksheet = utils.json_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, `訂單`);
    writeFile(workbook, `訂單創立清單結果.xlsx`);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const [elderOptions, deliveryStaffOptions, restaurantOptions] = await Promise.all([
        getElderIdSchema(null, null, clientId, true),
        getDeliveryStaffIdSchema(null, null, clientId, true, true, true),
        getRestaurantIdSchema(null, null, clientId, true),
      ]);
      const restaurantMeals = await Promise.all(
        restaurantOptions.map(async ({ id }) => asyncListAll(getRestaurantMealsByRestaurantByIsActive, { restaurantId: id })),
      );
      const restaurantMealsMap = restaurantMeals.reduce((acc, meals, index) => {
        acc[restaurantOptions[index].id] = meals;
        return acc;
      }, {});

      setRestaurantMealsMap(restaurantMealsMap);
      setElderOptions(elderOptions);
      setDeliveryStaffOptions(deliveryStaffOptions);
      setRestaurantOptions(restaurantOptions);
      if (restaurantOptions[0]) {
        setTargetRestaurantId(restaurantOptions[0].id);
      } else {
        setTargetRestaurantId();
      }

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!targetRestaurantId) return;

    (async () => {
      setIsLoading(true);

      const restaurantMeals = await asyncListAll(getRestaurantMealsByRestaurantByIsActive, { restaurantId: targetRestaurantId });

      const newTargetOrders = targetOrders.map((order) => {
        const { mealItems } = order;
        if (mealItems) {
          mealItems.forEach((mealItem) => {
            const existingRestaurantMeal = restaurantMeals.find(({ name }) => name === mealItem.name);
            if (existingRestaurantMeal) {
              if (!mealItem.price) {
                mealItem.price = existingRestaurantMeal.price;
              }
              if (!mealItem.cost) {
                mealItem.cost = existingRestaurantMeal.cost;
              }
            } else {
              mealItem.price = undefined;
              mealItem.cost = undefined;
            }
          });
          const total = mealItems.reduce((acc, mealItem) => acc + mealItem.price * mealItem.quantity, 0);
          const totalCost = mealItems.reduce((acc, mealItem) => acc + mealItem.cost * mealItem.quantity, 0);
          order.total = total;
          order.totalCost = totalCost;
        }
        return order;
      });

      setTargetOrders(newTargetOrders);
      // setRestaurantMealOptions(restaurantMeals);
      setIsLoading(false);
    })();
  }, [targetRestaurantId]);

  const validateOrders = () => {
    const orderProblems = [];
    const addOrderProblem = (referenceId, error, warning) => {
      const existingProblem = orderProblems.find((problem) => problem.referenceId === referenceId);
      if (existingProblem) {
        existingProblem.errors = [...new Set([...existingProblem.errors, error])];
        existingProblem.warnings = [...new Set([...existingProblem.warnings, warning])];
      } else {
        orderProblems.push({
          referenceId,
          errors: error ? [error] : [],
          warnings: warning ? [warning] : [],
        });
      }
    };

    targetOrders.forEach((order) => {
      if (order.deliveryDatetimes.length === 0) {
        addOrderProblem(order.referenceId, '未設定送餐時間');
      } else {
        const deliveryDatetime = order.deliveryDatetimes[0];
        const today = moment().startOf('day');
        const deliveryDate = moment(deliveryDatetime).startOf('day');
        const thirtyDaysLater = moment().add(30, 'days').startOf('day');
        if (deliveryDate.isBefore(today) || deliveryDate.isAfter(thirtyDaysLater)) {
          addOrderProblem(order.referenceId, '送餐時間不正確，請設定30天內的訂單');
        }
      }
      if (!order.elder) {
        addOrderProblem(order.referenceId, '未設定送餐對象');
      }
      if (!order.deliveryStaffId) {
        addOrderProblem(order.referenceId, undefined, '未設定送餐大使');
      }
      if (!order.restaurantId) {
        addOrderProblem(order.referenceId, '未設定餐廳');
      } else {
        if (!order.mealItems || order.mealItems.length === 0) {
          addOrderProblem(order.referenceId, '未設定餐點');
        }
        if (order.mealItems.some((mealItem) =>
          mealItem.price === undefined ||
            mealItem.cost === undefined ||
            mealItem.name === undefined,
        )) {
          addOrderProblem(order.referenceId, '餐點單價或進價未設定，請在餐點頁面設定');
        }
        if (order.mealItems.some((mealItem) =>
          mealItem.quantity === undefined ||
          mealItem.quantity <= 0,
        )) {
          addOrderProblem(order.referenceId, '餐點數量未設定');
        }
      }
    });
    setOrderProblems(orderProblems);
  };

  useEffect(() => {
    const elders = targetOrders.filter(({ elder }) => elder).map(({ elder }) => elder);
    const uniqueElders = elders.reduce((current, obj) => {
      const index = current.findIndex(({ name, address: { county, district, street } }) =>
        name === obj.name &&
        county === obj.address.county &&
        district === obj.address.district &&
        street === obj.address.street);
      if (index === -1) {
        current.push(obj);
      }
      return current;
    }, []);
    validateOrders();
    setUniqueElders(uniqueElders);
    setLocations([]);
  }, [targetOrders]);

  const unrecognizedAddress = uniqueElders.reduce((acc, elder) => {
    if (!elder.address?.lat || !elder.address?.lng) {
      return acc + 1;
    }
    return acc;
  }, 0);

  const onRowSelectionChange = (rowsSelected = []) => {
    const newLocations = [];
    targetOrders.forEach((order, index) => {
      if (rowsSelected.includes(index)) {
        if (order.elder?.address?.lat && order.elder?.address?.lng) {
          const existingLocation = newLocations.find((location) => (
            location.lat === order.elder.address.lat && location.lng === order.elder.address.lng
          ));
          if (existingLocation) {
            existingLocation.name += ', ' + (order.referenceId || '').split('-')[1] || order.referenceId || '';
          } else {
            newLocations.push({
              lat: order.elder.address.lat,
              lng: order.elder.address.lng,
              name: (order.referenceId || '').split('-')[1] || order.referenceId || '',
            });
          }
        }
      }
    });
    setLocations(newLocations);
  };

  const downloadOrderTemplate = async () => {
    const fileResponse = await fetch('/documents/訂單上傳範本v3.xlsx');
    if (!fileResponse.ok) {
      throw new Error('下載檔案失敗');
    }
    const blob = await fileResponse.blob();
    download('訂單上傳範本v3.xlsx', blob);
  };

  return (
    <div className={classes.app}>
      <div className={`${classes.centerContent} ${showMap || showHistory ? classes.centerContentShrink : ''}`}>
        <Grid container direction="row" alignItems="flex-start">
          <Grid container item xs={12} spacing={4}
            direction="row" justifyContent="flex-end" style={{ backgroundColor: Colors.highlight, margin: 8 }}>
            {needRestaurantId &&
            <Grid item xs={4}>
              {restaurantOptions && restaurantOptions.length !== 0 &&
              <FormControl fullWidth>
                <AutoSelect
                  id={`restaurantId`}
                  options={restaurantOptions.map(({ name }) => name)}
                  values={restaurantOptions.map(({ id }) => id)}
                  required={false}
                  value={targetRestaurantId}
                  label="餐廳"
                  onChange={(newItem) => {
                    setTargetRestaurantId(newItem);
                  }}
                  freeSolo={false}
                  disabled={isLoading}
                />
              </FormControl>}
            </Grid>}
            <Grid item xs={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showMap}
                    name={'showMap'}
                    value={showMap}
                    onChange={(e) => {
                      setShowMap(e.target.checked);
                      if (e.target.checked) {
                        setShowHistory(false);
                      }
                    }}
                  />
                }
                label={'顯示地圖'}
              />
            </Grid>
            <Grid item xs={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showHistory}
                    name={'showHistory'}
                    value={showHistory}
                    onChange={(e) => {
                      setShowHistory(e.target.checked);
                      if (e.target.checked) {
                        setShowMap(false);
                      }
                    }}
                  />
                }
                label={'顯示上傳紀錄'}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2} style={{ margin: 8 }}>
            {targetOrders.length == 0 &&
              <Grid item container xs={12} spacing={2} alignItems="flex-end">
                <Grid item>
                  <FileUploader
                    isLoading={isLoading}
                    onDataChanged={({ file, data }) => {
                      const excelData = getExecelDataAdapter(data);
                      if (excelData) {
                        const newTargetOrders = excelData.getFormattedData();
                        setNeedRestaurantId(excelData.getNeedRestaurantId());
                        setNeedCheckAddress(excelData.getNeedCheckAddress());
                        newTargetOrders.forEach((order) => {
                          const { elder = {}, deliveryStaffName, restaurantName, mealItems } = order;
                          if (elder.identificationCardId) {
                            const existingElder = elderOptions.find(({ identificationCardId }) => identificationCardId === elder.identificationCardId);
                            if (existingElder) {
                              order.elder = existingElder;
                            } else {
                              // 如果沒有身分證字號當成沒有送餐對象
                              order.elder = undefined;
                            }
                          } else {
                            const existingElder = elderOptions.find(({ name, address }) =>
                              name === elder.name &&
                              address.county === elder.address.county &&
                              address.district === elder.address.district &&
                              address.street === elder.address.street);
                            if (existingElder && (!elder.address.lat || !elder.address.lng)) {
                              elder.address.lat = existingElder.address.lat;
                              elder.address.lng = existingElder.address.lng;
                              elder.address.note = existingElder.address.note;
                            }
                          }
                          if (deliveryStaffName) {
                            const existingDeliveryStaff = deliveryStaffOptions.find(({ name }) => name === deliveryStaffName);
                            if (existingDeliveryStaff) {
                              order.deliveryStaffId = existingDeliveryStaff.id;
                            }
                          }
                          if (restaurantName) {
                            const existingRestaurant = restaurantOptions.find(({ name }) => name === restaurantName);
                            if (existingRestaurant) {
                              order.restaurantId = existingRestaurant.id;
                            }
                          }
                          if (mealItems && order.restaurantId) {
                            mealItems.forEach((mealItem) => {
                              const existingRestaurantMeal = restaurantMealsMap[order.restaurantId].find(({ name }) => name === mealItem.name);
                              if (existingRestaurantMeal) {
                                if (!mealItem.price) {
                                  mealItem.price = existingRestaurantMeal.price;
                                }
                                if (!mealItem.cost) {
                                  mealItem.cost = existingRestaurantMeal.cost;
                                }
                              }
                            });
                            const total = mealItems.reduce((acc, mealItem) => acc + mealItem.price * mealItem.quantity, 0);
                            const totalCost = mealItems.reduce((acc, mealItem) => acc + mealItem.cost * mealItem.quantity, 0);
                            order.total = total;
                            order.totalCost = totalCost;
                          }
                        });

                        const errors = excelData.getErrors();
                        setFileErrors(errors);
                        setTargetOrders(newTargetOrders);
                        setFile(file);
                      } else {
                        setTargetOrders([]);
                        setUniqueElders([]);
                        toastr.error('無法辨識上傳的檔案');
                      }
                    }}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={downloadOrderTemplate}
                    style={{ marginTop: '10px', marginLeft: '10px' }}
                  >
                    下載EXCEL範例
                  </Button>
                </Grid>
              </Grid>
            }
            {fileErrors.length !== 0 &&
              <Grid item xs={12}>
                {fileErrors.map((error, index) => (
                  <Typography key={index} variant='h6' color='error'>{error}</Typography>
                ))}
              </Grid>
            }
            {targetOrders.length !== 0 &&
              <Grid item xs={12}>
                <TargetOrderTable
                  data={targetOrders}
                  deliveryStaffOptions={deliveryStaffOptions}
                  restaurantOptions={restaurantOptions}
                  onRowSelectionChange={onRowSelectionChange}
                  onUpdate={(data) => {
                    setTargetOrders(data);
                  }}
                />
              </Grid>
            }
            {orderProblems.length !== 0 &&
            <Grid item xs={12}>
              <OrderProblemTable
                data={orderProblems}
              />
            </Grid>}
          </Grid>

          {restaurantOptions && deliveryStaffOptions && elderOptions &&
            <Grid item container xs={12}>
              <div style={{ flex: 1, textAlign: 'center', lineHeight: '36px', marginTop: 10 }}>
                {checkAddressProgress == 0 && unrecognizedAddress !== 0 && `有${unrecognizedAddress}個地址尚未定位或定位失敗`}
                {checkAddressProgress !== 0 && `尚有${checkAddressProgress}個地址定位檢查中`}
              </div>
              {needCheckAddress &&
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading || targetOrders.length === 0}
                onClick={checkAddress}
                style={{ marginRight: 20, marginTop: 10 }}
              >
                定位送餐地址
              </Button>}
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading || targetOrders.length === 0 || orderProblems.some((problem) => problem.errors.length > 0)}
                onClick={handleSubmit}
                style={{ marginRight: 20, marginTop: 10 }}
              >
                送出
              </Button>
            </Grid>}
        </Grid>
        {openConfirmDialog &&
          <FormDialog
            title={'訂單列表'}
            openOnInit={true}
            fullScreen={true}
            hideCloseButton={true}
          >
            <Grid container spacing={2}>
              <Grid item container spacing={1}>
                {toCreateElders.length !== 0 &&
                  <Grid item xs={12} container direction='row' alignItems='center'>
                    <PersonAddIcon style={{ marginRight: 4 }}/>
                    <Typography variant="h5">
                      建立送餐對象
                    </Typography>
                  </Grid>}
                {
                  toCreateElders.map((elder, index) => {
                    const matched = createElderResults.find(({ id }) => id === elder.id);
                    return (
                      <Grid item container sm={4} md={3} lg={2} key={`createElders-${index}`} spacing={1} style={{ marginLeft: 20 }}>
                        <Grid item xs={12} container align="center">
                          <Grid item xs={12} container align="center">
                            {isLoading && !matched ? <CircularProgress color="primary" size={24} /> :
                              matched ?
                                <CheckIcon color="primary" /> : <CancelIcon color="secondary" />}
                            <Typography style={{ marginTop: 2, marginLeft: 4 }}>
                              {elder.name}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h5" component="p" color="secondary">
                            {(createElderErrors.find(({ elderId })=> elderId === elder.id) || {}).message || ''}
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  })
                }
              </Grid>
              <Grid item container spacing={1}>
                {toUpdateElders.length !== 0 &&
                  <Grid item xs={12} container direction='row' alignItems='center'>
                    <EditIcon style={{ marginRight: 4 }}/>
                    <Typography variant="h5">
                      更新送餐對象
                    </Typography>
                  </Grid>}
                {
                  toUpdateElders.map((elder, index) => {
                    const matched = updateElderResults.find(({ id }) => id === elder.id);
                    return (
                      <Grid item container sm={4} md={3} lg={2} key={`updateElders-${index}`} spacing={1} style={{ marginLeft: 20 }}>
                        <Grid item xs={12} container align="center">
                          <Grid item xs={12} container align="center">
                            {isLoading && !matched ? <CircularProgress color="primary" size={24} /> :
                              matched ?
                                <CheckIcon color="primary" /> : <CancelIcon color="secondary" />}
                            <Typography style={{ marginTop: 2, marginLeft: 4 }}>
                              {elder.name}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="h5" component="p" color="secondary">
                            {(updateElderErrors.find(({ elderId })=> elderId === elder.id) || {}).message || ''}
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  })
                }
              </Grid>
              <Grid item container spacing={1}>
                {toCreateOrders.length !== 0 &&
                  <Grid item xs={12} container direction='row' alignItems='center'>
                    <ListAltIcon style={{ marginRight: 4 }}/>
                    <Typography variant="h5">
                      建立訂單
                    </Typography>
                  </Grid>}
                {
                  toCreateOrders.map((order, index) => (
                    <Grid item container xs={12} key={`createOrder-${index}`} spacing={1} style={{ marginLeft: 20 }}>
                      <Grid item xs={12}>
                        <Typography variant="h6" component="p">
                          {(elderOptions.find(({ id })=> id === order.elderId) || {}).name || ''}
                        </Typography>
                      </Grid>
                      {order.deliveryDatetimes.map((datetime) => {
                        const matched = createOrderResults.find(({ elderId, deliveryBy }) => elderId === order.elderId && deliveryBy === datetime);
                        return (
                          <Grid item sm={4} md={3} lg={2} container align="center" key={datetime}>
                            <Grid item xs={12} container align="center">
                              {isLoading && !matched ? <CircularProgress color="primary" size={24} /> :
                                matched ?
                                  <CheckIcon color="primary" /> : <CancelIcon color="secondary" />}
                              <Typography style={{ marginTop: 2, marginLeft: 4 }}>
                                {formatDatetime(datetime, { weekday: false })}
                              </Typography>
                            </Grid>
                          </Grid>
                        );
                      })}
                      <Grid item xs={12}>
                        <Typography variant="h5" component="p" color="secondary">
                          {(createOrderErrors.find(({ elderId })=> elderId === order.elderId) || {}).message || ''}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))
                }
              </Grid>
              <Grid item container xs={12} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={triggerDownload}
                  disabled={isLoading}
                  style={{ marginRight: 16 }}
                >
                  下載結果
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  onClick={handleComplete}
                >
                  關閉視窗
                  {isLoading && <CircularProgress color="primary" size={16} style={{ marginLeft: 8 }} />}
                </Button>
              </Grid>
            </Grid>
          </FormDialog>
        }
      </div>
      <div className={`${classes.popup} ${showMap ? classes.popupActive : ''}`}>
        <Marker locations={locations} showMap={showMap} />
      </div>
      <div className={`${classes.popup} ${showHistory ? classes.popupActive : ''}`}>
        <UploadHistoryTable clientId={clientId} />
      </div>
    </div>
  );
}

AdminCreateBulkOrderForm.propTypes = {
  onComplete: PropTypes.func,
};
